/**
 * Routy usage tracker (client side).
 *
 * Records lightweight usage events (a chat turn, a plan, an appointment
 * briefing, or a request/booking card summary) so adoption/volume can be
 * monitored in the admin area. Events are
 * fire-and-forget POSTs to a backend telemetry endpoint — failures (including
 * the endpoint not existing yet, 404/501) are swallowed so tracking never
 * affects the user experience or Routy's behavior.
 *
 * Backend: POST /api/app/routyUsage/track  (see
 * docs/implementation/routy-usage-backend-spec.md). Until that ships this is a
 * silent no-op; once it's live, events flow and the admin Routy Usage page
 * (RoutyUsageManager) surfaces the aggregates.
 */
import { request } from '../../api/core/request';
import { OpenAPI } from '../../api/core/OpenAPI';

export type RoutyUsageSurface =
  | 'chat'
  | 'appointment_summary'
  | 'request_summary'
  | 'booking_summary';

export interface RoutyUsageEvent {
  /** Where the usage happened. */
  surface: RoutyUsageSurface;
  /** Whether the turn/generation succeeded, errored, or fell back. */
  outcome: 'ok' | 'error' | 'fallback';
  /** Which model/provider served it, when known. */
  provider?: 'Claude' | 'OpenAI' | 'Fallback' | 'Cache';
  /** End-to-end latency in milliseconds, when measured. */
  latencyMs?: number;
  /** Number of tool calls the turn made (chat only), when known. */
  toolCount?: number;
  /** For appointment summaries: whether it recognized a sales opportunity
   * (i.e. a Sales Prep block was rendered). */
  isSalesOpportunity?: boolean;
  /** For appointment summaries: which generation the user asked for —
   * 'summary' (fast, no sales analysis) or 'salesPrep' (full briefing). */
  mode?: 'summary' | 'salesPrep';
}

/**
 * Record a Routy usage event. Never throws and never blocks — safe to call
 * inline after a turn completes.
 */
export function recordRoutyUsage(event: RoutyUsageEvent): void {
  try {
    void request(OpenAPI, {
      method: 'POST',
      url: '/api/app/routyUsage/track',
      body: { ...event, clientTimestamp: new Date().toISOString() },
      mediaType: 'application/json',
    }).catch(() => {
      // Endpoint not live yet or transient failure — telemetry is best-effort.
    });
  } catch {
    // Never let usage tracking surface an error to the caller.
  }
}
